/**
 * SkillTaskDashboard - Split-screen panel showing skill task progress
 * Data comes from /conversations/{id}/board API + board_state WS events
 * Renders as split-screen (like ArtifactViewer) instead of a modal
 */
import { useEffect, useRef, useState } from 'react';
import {
  X,
  CheckCircle2,
  Loader2,
  Clock,
  Zap,
  AlertCircle,
  Ban,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ConfirmDialog from '@/components/ConfirmDialog';
import {
  useSkillTaskStore,
  type BoardState,
  type BoardStep,
} from '@/stores/skillTaskStore';
import { useConversationsStore } from '@/stores/conversationsStore';

function StepRow({
  step,
  index,
  isPaused = false,
  pausedAt,
}: {
  step: BoardStep;
  index: number;
  isPaused?: boolean;
  pausedAt?: number; // 🔥 store 层面的暂停时间戳（waiting 状态），持久化不受组件卸载影响
}) {
  const isDone = !!step.finishedAt && !step.error;
  const hasError = !!step.error;
  // 🔥 isPaused 时停止计时动画（board status === 'waiting' 或 'cancelled'）
  const isActive =
    !!step.startedAt && !step.finishedAt && !step.error && !isPaused;
  const isPending = !step.startedAt;

  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => setTick(t => t + 1), 100);
    return () => clearInterval(interval);
  }, [isActive]);

  const elapsed = (() => {
    if (!step.startedAt) return null;
    const start = new Date(step.startedAt).getTime();
    // 🔥 优先级：finishedAt > store 的 pausedAt > Date.now()
    // cancelled 的步骤在 store 中已被补上 finishedAt，不会走到 Date.now()
    // waiting 的步骤使用 store 的 pausedAt 冻结时间，不依赖组件 ref
    const end = step.finishedAt
      ? new Date(step.finishedAt).getTime()
      : pausedAt
        ? pausedAt
        : Date.now();
    const diff = (end - start) / 1000;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = Math.floor(diff % 60);
    const parts: string[] = [];
    if (h > 0) parts.push(`${h} h`);
    if (m > 0) parts.push(`${m} min`);
    parts.push(`${s} s`);
    return parts.join(' ');
  })();

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-all',
        isActive && 'bg-blue-50 font-medium',
        isDone && 'text-gray-500',
        hasError && 'bg-red-50',
        isPending && 'text-gray-400'
      )}
    >
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
        {isDone ? (
          <CheckCircle2 size={16} className="text-green-500" />
        ) : hasError ? (
          <AlertCircle size={16} className="text-red-500" />
        ) : isActive ? (
          <Loader2 size={16} className="text-blue-500 animate-spin" />
        ) : (
          <Clock size={16} className="text-gray-300" />
        )}
      </div>
      <span
        className={cn(
          'flex-1',
          isActive && 'text-blue-700',
          isDone && 'text-green-700',
          hasError && 'text-red-700',
          isPending && 'text-gray-400'
        )}
      >
        {index + 1}. {step.name || `Step ${index + 1}`}
      </span>
      {elapsed && (
        <span
          className={cn(
            'text-xs tabular-nums flex-shrink-0',
            isDone
              ? 'text-gray-400'
              : hasError
                ? 'text-red-400'
                : isPaused
                  ? 'text-gray-400'
                  : 'text-blue-500'
          )}
        >
          {elapsed}
        </span>
      )}
    </div>
  );
}

/**
 * 从对话消息中提取第一条用户消息内容，去掉 [skill:xxx] 关键字
 */
function useBoardTitle(conversationId: string): string {
  const conversations = useConversationsStore(state => state.conversations);
  const conv = conversations.find(
    c => c.id === conversationId || c.conversationId === conversationId
  );
  if (conv?.messages && conv.messages.length > 0) {
    const firstUserMsg = conv.messages.find(m => m.role === 'user');
    if (firstUserMsg?.choices?.[0]?.blocks) {
      const textBlock = firstUserMsg.choices[0].blocks.find(
        b => b.type === 'text'
      );
      if (textBlock?.content) {
        // 去掉 [skill:xxx] 关键字
        return (
          textBlock.content.replace(/\[skill:\w+\]\s*/gi, '').trim() ||
          conversationId.substring(0, 8) + '...'
        );
      }
    }
  }
  // fallback: 使用对话标题或 ID
  return conv?.title || conversationId.substring(0, 8) + '...';
}

function BoardCard({
  board,
  onCancel,
}: {
  board: BoardState;
  onCancel?: (conversationId: string) => void;
}) {
  // 🔥 使用后端返回的 stepCount（优先），fallback 到 steps.length
  const totalSteps = board.stepCount ?? board.steps.length;
  // 🔥 board 整体状态：waiting 时暂停计时
  const isWaiting = board.status === 'waiting';
  const isCancelled = board.status === 'cancelled';
  const completedSteps = board.steps.filter(
    s => s.finishedAt && !s.error
  ).length;
  const hasError = board.steps.some(s => s.error);
  const isAllDone =
    completedSteps === totalSteps && totalSteps > 0 && !isCancelled;
  const hasRunning =
    board.steps.some(s => s.startedAt && !s.finishedAt && !s.error) &&
    !isWaiting &&
    !isCancelled;

  // 🔥 计算总耗时（从第一个步骤的 startedAt 到最后一个步骤的 finishedAt）
  const totalElapsed = (() => {
    if (!isAllDone || board.steps.length === 0) return null;
    const startTimes = board.steps
      .filter(s => s.startedAt)
      .map(s => new Date(s.startedAt!).getTime());
    const endTimes = board.steps
      .filter(s => s.finishedAt)
      .map(s => new Date(s.finishedAt!).getTime());
    if (startTimes.length === 0 || endTimes.length === 0) return null;
    const earliest = Math.min(...startTimes);
    const latest = Math.max(...endTimes);
    const diff = (latest - earliest) / 1000;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = Math.floor(diff % 60);
    const parts: string[] = [];
    if (h > 0) parts.push(`${h} h`);
    if (m > 0) parts.push(`${m} min`);
    parts.push(`${s} s`);
    return parts.join(' ');
  })();

  return (
    <div
      className={cn(
        'rounded-lg border p-4 mb-3 transition-all',
        isCancelled
          ? 'border-gray-300 bg-gray-50/50'
          : isAllDone
            ? 'border-green-200 bg-green-50/50'
            : hasError
              ? 'border-red-200 bg-red-50/50'
              : 'border-blue-200 bg-white shadow-sm'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold',
              isCancelled
                ? 'bg-gray-200 text-gray-500'
                : isAllDone
                  ? 'bg-green-100 text-green-700'
                  : hasError
                    ? 'bg-red-100 text-red-700'
                    : 'bg-blue-100 text-blue-700'
            )}
          >
            <Zap size={14} />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
              {useBoardTitle(board.conversationId)}
            </div>
            <div className="text-xs text-gray-500">
              {board.fetchedAt.toLocaleTimeString()}
              {isAllDone && totalElapsed && (
                <span> → Total: {totalElapsed}</span>
              )}
            </div>
          </div>
        </div>
        {isCancelled ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
            <Ban size={12} /> Cancelled
          </span>
        ) : isAllDone ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
            <CheckCircle2 size={12} /> Done
          </span>
        ) : isWaiting ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
            <Clock size={12} /> Waiting
          </span>
        ) : hasRunning ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
            <Loader2 size={12} className="animate-spin" /> Running
          </span>
        ) : hasError ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
            <AlertCircle size={12} /> Error
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Pending
          </span>
        )}
      </div>

      <div className="space-y-1">
        {board.steps.map((step, idx) => (
          <StepRow
            key={idx}
            step={step}
            index={idx}
            isPaused={isWaiting || isCancelled}
            pausedAt={board.pausedAt}
          />
        ))}
      </div>

      {totalSteps > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 min-w-0 bg-gray-200 rounded-full h-1.5">
            <div
              className={cn(
                'h-1.5 rounded-full transition-all duration-500',
                isCancelled
                  ? 'bg-gray-400'
                  : isAllDone
                    ? 'bg-green-500'
                    : hasError
                      ? 'bg-red-500'
                      : 'bg-blue-500'
              )}
              style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 flex-shrink-0">
            {completedSteps}/{totalSteps}
          </div>
          {hasRunning && (
            <button
              onClick={e => {
                e.stopPropagation();
                onCancel?.(board.conversationId);
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border border-red-300 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 hover:border-red-400 transition-colors cursor-pointer flex-shrink-0"
            >
              <Ban size={10} /> Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface SkillTaskDashboardProps {
  mode?: 'split' | 'overlay';
  conversationId?: string; // 🔥 只显示当前对话的 board
}

export default function SkillTaskDashboard({
  mode = 'overlay',
  conversationId,
}: SkillTaskDashboardProps) {
  const { boards, setDashboardOpen, cancelBoard } = useSkillTaskStore();

  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancelBoard = (conversationId: string) => {
    setCancelTarget(conversationId);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      await cancelBoard(cancelTarget);
    } finally {
      setIsCancelling(false);
      setCancelTarget(null);
    }
  };
  const panelRef = useRef<HTMLDivElement>(null);
  // 🔥 如果传入了 conversationId，只显示当前对话的 board；否则显示所有
  const allBoards = conversationId
    ? Object.values(boards).filter(b => b.conversationId === conversationId)
    : Object.values(boards);

  const cancelledBoards = allBoards.filter(b => b.status === 'cancelled');
  const runningBoards = allBoards.filter(
    b =>
      b.status !== 'cancelled' &&
      b.steps.some(s => s.startedAt && !s.finishedAt && !s.error)
  );
  const completedBoards = allBoards.filter(
    b =>
      b.status !== 'cancelled' &&
      b.steps.length > 0 &&
      b.steps.every(s => s.finishedAt)
  );

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDashboardOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setDashboardOpen]);

  // Split mode: inline panel (like ArtifactViewer split)
  if (mode === 'split') {
    return (
      <div
        ref={panelRef}
        className="w-[380px] max-w-[50vw] flex-shrink-0 border-l border-gray-200 bg-white flex flex-col h-full overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-amber-500" />
            <h2 className="text-sm font-bold text-gray-900">Tasks Status</h2>
            {runningBoards.length > 0 && (
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                {runningBoards.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setDashboardOpen(false)}
            className="p-1 mr-8 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {allBoards.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Zap size={36} className="mb-2 text-gray-300" />
              <p className="text-xs font-medium">No skill tasks yet</p>
            </div>
          ) : (
            <>
              {runningBoards.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Running ({runningBoards.length})
                  </h3>
                  {runningBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
              {cancelledBoards.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Cancelled ({cancelledBoards.length})
                  </h3>
                  {cancelledBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
              {completedBoards.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Completed ({completedBoards.length})
                  </h3>
                  {completedBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Cancel confirmation dialog */}
        <ConfirmDialog
          isOpen={!!cancelTarget}
          title="Cancel Task"
          message="Are you sure you want to cancel this running task? This action cannot be undone."
          confirmText="Cancel Task"
          cancelText="Keep Running"
          type="danger"
          isLoading={isCancelling}
          onConfirm={handleConfirmCancel}
          onCancel={() => setCancelTarget(null)}
        />
      </div>
    );
  }

  // Overlay mode: floating panel (for small screens)
  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={() => setDashboardOpen(false)}
      />
      <div
        ref={panelRef}
        className="fixed right-0 top-0 bottom-0 w-[380px] max-w-[90vw] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 animate-in slide-in-from-right duration-200"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Zap size={18} className="text-amber-500" />
            <h2 className="text-base font-bold text-gray-900">Tasks Status</h2>
            {runningBoards.length > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                {runningBoards.length} running
              </span>
            )}
          </div>
          <button
            onClick={() => setDashboardOpen(false)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {allBoards.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Zap size={48} className="mb-3 text-gray-300" />
              <p className="text-sm font-medium">No skill tasks yet</p>
              <p className="text-xs mt-1">
                Type{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                  /skill
                </code>{' '}
                to start
              </p>
            </div>
          ) : (
            <>
              {runningBoards.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Running ({runningBoards.length})
                  </h3>
                  {runningBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
              {cancelledBoards.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Cancelled ({cancelledBoards.length})
                  </h3>
                  {cancelledBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
              {completedBoards.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Completed ({completedBoards.length})
                  </h3>
                  {completedBoards.map(board => (
                    <BoardCard
                      key={board.conversationId}
                      board={board}
                      onCancel={handleCancelBoard}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        isOpen={!!cancelTarget}
        title="Cancel Task"
        message="Are you sure you want to cancel this running task? This action cannot be undone."
        confirmText="Cancel Task"
        cancelText="Keep Running"
        type="danger"
        isLoading={isCancelling}
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </>
  );
}
